import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import * as yup from 'yup';
import api from 'utils/api';
import { observeServiceAvailability } from 'utils/serviceAvailability';

const schema = yup.object({
  name: yup.string().trim().required('Enter your name.').max(200, 'Use 200 characters or fewer.'),
  email: yup.string().trim().required('Enter your email.').email('Enter a valid email address.').max(254, 'Use 254 characters or fewer.'),
  content: yup.string().trim().required('Describe the issue you need help with.').max(10000, 'Use 10,000 characters or fewer.'),
});

export default function KaiyleeContactDialog({ sessionId, onClose }) {
  const user = useSelector((state) => state.user?.details);
  const [values, setValues] = useState(() => ({
    name: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
    email: user?.email || '',
    content: '',
  }));
  const [contactPrefillFailed, setContactPrefillFailed] = useState(false);
  const [errors, setErrors] = useState({});
  const [sendError, setSendError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const dirty = useRef(new Set());
  const submitting = useRef(false);
  const active = useRef(true);
  const endpoint = sessionId ? `/chat/sessions/${encodeURIComponent(sessionId)}` : null;

  useEffect(() => {
    if (!endpoint) return undefined;
    let current = true;
    active.current = true;
    api.post(`${endpoint}/contact-draft`, {}).then((draft) => {
      if (!current) return;
      observeServiceAvailability(draft.availability);
      if (submitting.current) return;
      setValues((previous) => ({
        ...previous,
        name: dirty.current.has('name') ? previous.name : draft.name ?? previous.name,
        email: dirty.current.has('email') ? previous.email : draft.email ?? previous.email,
      }));
    }).catch(() => {
      if (current) setContactPrefillFailed(true);
    });
    return () => { current = false; active.current = false; };
  }, [endpoint]);

  const change = (field, value) => {
    dirty.current.add(field);
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: '' }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!endpoint || submitting.current) return;
    let payload;
    try {
      payload = schema.validateSync(values, { abortEarly: false });
    } catch (error) {
      setErrors(Object.fromEntries(error.inner.map((issue) => [issue.path, issue.message])));
      return;
    }
    submitting.current = true;
    setSending(true);
    setSendError('');
    try {
      await api.post(`${endpoint}/contact`, payload);
      if (active.current) setSent(true);
    } catch (_) {
      if (active.current) {
        setSendError("We couldn't confirm that your message was sent. Your message is still here. Please try again.");
        submitting.current = false;
      }
    } finally {
      if (active.current) setSending(false);
    }
  };

  return (
    <Dialog open onClose={sending ? undefined : onClose} fullWidth maxWidth="sm" sx={{ zIndex: 1500 }} aria-labelledby="kaiylee-contact-title">
      <DialogTitle id="kaiylee-contact-title">Contact our team</DialogTitle>
      {sent ? <>
        <DialogContent><Alert severity="success">Your message has been sent to our team. They can follow up at {values.email.trim()}.</Alert></DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </> : <form onSubmit={submit} noValidate>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">I can pass a message to our team for email follow-up. Please describe your issue and confirm your contact details below.</Typography>
            {!endpoint && <Typography role="status" variant="body2">Kaiylee is connecting. You can write your message while you wait.</Typography>}
            {contactPrefillFailed && <Alert severity="info">Please confirm your name and email before sending.</Alert>}
            <TextField label="Name" autoComplete="name" value={values.name} onChange={(event) => change('name', event.target.value)}
              required fullWidth disabled={sending} error={Boolean(errors.name)} helperText={errors.name} inputProps={{ maxLength: 200 }} />
            <TextField label="Email" type="email" autoComplete="email" value={values.email} onChange={(event) => change('email', event.target.value)}
              required fullWidth disabled={sending} error={Boolean(errors.email)} helperText={errors.email} inputProps={{ maxLength: 254 }} />
            <TextField label="Message" multiline minRows={4} value={values.content} onChange={(event) => change('content', event.target.value)}
              required fullWidth disabled={sending} error={Boolean(errors.content)} helperText={errors.content || 'Your message and a reference to this conversation will be sent to our team.'} inputProps={{ maxLength: 10000 }} />
            {sendError && <Alert severity="error">{sendError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={sending}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={sending || !endpoint}>{sending ? 'Sending…' : 'Send message'}</Button>
        </DialogActions>
      </form>}
    </Dialog>
  );
}
